import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CmEdPersonCardView} from "../../interface/cm-ed-person-card-view.interface";
import {CmEdSize} from "../../interface/cm-ed-size.type";
import {CmContractExternalDataFoldPt} from "../../interface/cm-contract-external-data-fold-pt.interface";
import {cmEdCountHits, cmEdHasHit} from "../cm-ed-search-hit.util";

/**
 * ONE person of the delivery as a card — role, name and the person's own figures.
 *
 * The role is an EYEBROW above the name rather than a suffix behind it: "Versicherungsnehmer" and
 * "Versicherte Person" are what a clerk scans a Personen list for, and a role hidden at the end of a
 * name that may already be ellipsised is a role nobody finds. Name and role arrive as two separate
 * fields, so nothing here parses a German string apart to style its parts.
 *
 * There is no second line under the name: the Personen block delivers a name and a role and nothing
 * else that belongs above the pairs. The card renders none, and the view model's `detail` member — a
 * leftover the host has always filled with `null` — is reported for deletion rather than kept alive
 * by a template branch that can never be reached.
 *
 * <h2>What it deliberately does NOT do</h2>
 * <ul>
 *   <li>It does not load anything and holds no service.</li>
 *   <li>It does not FORMAT: every value is printed as the server sent it.</li>
 *   <li>It does not link to the internal cm/cr person. The delivery is EXTERNAL data; the sheet
 *       shows what the partner sent, and matching that against an internal record is a different
 *       screen's job.</li>
 *   <li>It does not persist its expander, for the same reason the coverage card does not: a card
 *       that silently hides half its data on return is exactly the "es wurde nicht importiert"
 *       complaint this screen exists to end.</li>
 *   <li>It does not MATCH and it does not HIGHLIGHT a pair. The search runs once in the host; the
 *       card only reveals what a term found and states how much of it there is — see
 *       {@link searching}.</li>
 * </ul>
 */
@Component({
    selector: 'mvs-cm-ed-person-card',
    templateUrl: './cm-ed-person-card.component.html',
    styleUrls: ['./cm-ed-person-card.component.scss'],
    standalone: false,
})
export class CmEdPersonCardComponent implements OnChanges {

    /** Counts every instance ever created, so two cards never share a DOM id. */
    protected static instanceCounter: number = 0;

    /** The person to render. */
    @Input() person: CmEdPersonCardView;

    /** Width bucket, measured once by the host. Decides the type scale and the padding. */
    @Input() size: CmEdSize = 'lg';

    /**
     * Whether a search term is currently active — passed through to the pairs, and the switch for the
     * two things the card itself does about a search.
     *
     * The person card is the one a cross-link search lands on most often: someone typing "Anna Meier"
     * is looking for her Deckungen, and this card is where her name actually lives. It NEVER hides and
     * NEVER dims — a person with no hit renders exactly as it does at rest — so the two things it does
     * carry the whole signal: it states its hits, and it opens itself when the match is behind its
     * expander. The reasoning is written out once, on the coverage card's own `searching`.
     */
    @Input() searching: boolean = false;

    /**
     * THE READER'S OWN expander state — what the card returns to when the term is cleared.
     *
     * Never written by the automatic reveal; see {@link open}.
     */
    expanded: boolean = false;

    /**
     * The reader's decision DURING an active search — `null` while they have not made one.
     *
     * Reader intent wins over the automatic reveal, and is dropped whenever the term or the person
     * changes ({@link ngOnChanges}). Never persisted.
     */
    protected searchOverride: boolean | null = null;

    /** DOM id of the level-2 block — the `aria-controls` target of this card's own expander. */
    protected readonly level2Id: string;

    /** The `aria-expanded` value {@link foldPtCache} currently states; `null` before the first read. */
    private foldPtOpen: boolean | null = null;

    /** The cached pass-through — see {@link foldPt} for why it is cached rather than rebuilt. */
    private foldPtCache: CmContractExternalDataFoldPt | null = null;

    constructor() {
        this.level2Id = 'cmEdPerson' + (++CmEdPersonCardComponent.instanceCounter) + '-more';
    }

    /**
     * A card sized to its bucket: tighter padding where the grid puts a card into one column.
     *
     * All six steps written out — the same rule the Deckung and object cards state, so three cards
     * side by side in one grid cannot answer a new step differently.
     */
    get padClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'p-3';
            case 'xs':
                return 'p-2';
        }
    }

    /**
     * Padding of the header BAND — the same horizontal step as {@link padClass} and a smaller
     * vertical one, so the eyebrow and the name share the left edge of the figures below while the
     * band still reads as a title strip rather than as a second panel.
     *
     * Deliberately identical to the Deckung and Objekt cards' rule: the three sit in one grid and a
     * band of a different height would make them read as different kinds of thing.
     */
    get headClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'px-3 py-2';
            case 'xs':
                return 'px-2 py-1';
        }
    }

    /**
     * Type scale of the person's name.
     *
     * `text-base` from `sm` up, i.e. from 560 px. The old rule read `xs || sm` and meant "below
     * 600 px"; `sm` now reaches 759 px, where a card is col-12 and therefore the widest card on the
     * ladder — and a person card's heading is a NAME, the one line that identifies the card.
     */
    get nameClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return 'text-base';
            case 'xs':
                return 'text-sm';
        }
    }

    /**
     * True while a term is active AND this person's Weitere Angaben hold one of its matches.
     *
     * Aggregates the flag the HOST set on each field; it folds nothing and compares nothing — the
     * sheet has exactly one matcher (`cm-ed-search-hit.util.ts`).
     */
    get level2Hit(): boolean {
        return this.searching && cmEdHasHit(this.person?.level2);
    }

    /**
     * True when the term matched the person's NAME or their ROLE rather than one of their pairs — the
     * switch for the mark on the card's heading.
     *
     * A person's name is the most searched string on this whole sheet ("wo ist Anna Meier
     * versichert?"), the host counts it into {@link CmEdPersonCardView#hitCount}, and until now the
     * card answered with a number beside an unchanged name. The count pointed at nothing, which on a
     * screen built to prove that nothing is hidden is the one failure that undoes the screen.
     *
     * Read by SUBTRACTION from the flags the host wrote — the card's total minus the hits its rendered
     * pairs account for — because the sheet has ONE matcher, in the host, and this component neither
     * sees the term nor ever may. Exact in every combination: the host builds the total as
     * `countTextHits(name, role) + fieldHits(level1) + fieldHits(level2)`.
     *
     * Both texts take the mark, because the total does not say which of the two carried the term. That
     * is a heading-level statement and the template says so; marking the exact characters needs
     * highlight SEGMENTS for the name and the role, which is a host change.
     */
    get ownTextHit(): boolean {

        if (!this.searching || !this.person) {
            return false;
        }

        const pairs = cmEdCountHits(this.person.level1) + cmEdCountHits(this.person.level2);

        return this.person.hitCount > pairs;
    }

    /**
     * Whether the level-2 block is on screen — the reader's own state, temporarily overruled by a
     * search that found something behind the expander.
     *
     * An OVERLAY and never a write, so clearing the term returns the card to exactly the state its
     * reader left it in; a click always wins. The full reasoning is on the coverage card's `open`.
     */
    get open(): boolean {

        if (!this.searching) {
            return this.expanded;
        }

        return this.searchOverride ?? (this.expanded || this.level2Hit);
    }

    /**
     * The expander's `aria-expanded` / `aria-controls`; see {@link CmContractExternalDataFoldPt}.
     *
     * Cached on the value it states, so the object identity `[pt]` watches changes exactly when
     * {@link open} does — the reader's click, the search flipping, and the host recomputing hits on
     * the same person object are three separate causes, and rebuilding on toggle alone covered one.
     */
    protected get foldPt(): CmContractExternalDataFoldPt {

        const open = this.open;

        if (this.foldPtOpen !== open || !this.foldPtCache) {
            this.foldPtOpen = open;
            this.foldPtCache = {
                root: {
                    'aria-expanded': String(open),
                    'aria-controls': this.level2Id,
                },
            };
        }

        return this.foldPtCache;
    }

    /**
     * Drops the reader's search-time override whenever the question changes — a new term, a search
     * switched on or off, or a new person. {@link expanded} is deliberately left alone.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (changes["searching"] || changes["person"]) {
            this.searchOverride = null;
        }
    }

    /**
     * Opens / closes the level-2 block.
     *
     * While a term is active the click is recorded as an OVERRIDE rather than as the reader's resting
     * state, so a card the search opened can be closed and a card closed while searching does not stay
     * shut once the term is cleared.
     */
    toggle(): void {

        if (this.searching) {
            this.searchOverride = !this.open;
            return;
        }

        this.expanded = !this.expanded;
    }
}
